import type { ApolloError } from "@apollo/client";

import {
  ActivitySortOrder,
  CallPlanInfoFragment,
  type GetActivitiesQuery,
  type GetActivityTypesQuery,
  type GetCallPlanActivitiesQuery,
} from "@/graphql/graphql";
import {
  addCalendarDays,
  formatLocalCalendarDateToday,
  toEndDateTimeUtc,
  toStartDateTimeUtc,
} from "@/utils/date-utils";

type PlanActivityRow = GetActivitiesQuery["activities"]["activities"][number];
type CallPlanInteractions = GetCallPlanActivitiesQuery["callPlan"]["interactions"];
type Program = CallPlanInfoFragment["program"];
type CallPlanActivityInteraction = Extract<
  NonNullable<NonNullable<CallPlanInteractions>[number]>,
  { __typename?: "ActivityInteraction" }
>;

export interface CallPlanFormValues {
  startDate: string;
  endDate: string;
}

/** Days from start to end for new/edit defaults (one week). */
export const CALL_PLAN_DURATION_DAYS = 7;

export const CALL_PLAN_TITLE_SEARCH_DEBOUNCE_MS = 500;

export type CallPlanModalActivityType = GetActivityTypesQuery["activityTypes"][number];

export const compareActivityTypes = (
  a: CallPlanModalActivityType,
  b: CallPlanModalActivityType
): number =>
  a.displayOrder - b.displayOrder || a.activityTypeLabel.localeCompare(b.activityTypeLabel);

export const getCallPlanSubmitButtonLabel = ({
  createLoading,
  updateLoading,
  callPlanToEdit,
}: {
  createLoading: boolean;
  updateLoading: boolean;
  callPlanToEdit?: CallPlanInfoFragment | null;
}): string => {
  if (createLoading) {
    return "Creating...";
  }
  if (updateLoading) {
    return "Saving...";
  }
  if (callPlanToEdit) {
    return "Save Changes";
  }

  return "Create Call Plan";
};

export const formatEnumLabel = (value: string): string =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const sortOrderToMui = (order: ActivitySortOrder): "asc" | "desc" =>
  order === ActivitySortOrder.Asc ? "asc" : "desc";

export const MAX_CALL_PLAN_ACTIVITIES = 7;

/** Maps call-plan interaction steps to the same row shape as GetActivities (picker table / selected list). */
export const callPlanInteractionsToPlanActivityRows = (
  interactions?: CallPlanInteractions | null
): PlanActivityRow[] => {
  if (!interactions?.length) {
    return [];
  }

  return interactions
    .filter(
      (i): i is CallPlanActivityInteraction =>
        i.__typename === "ActivityInteraction" && !!i.activityId
    )
    .sort((a, b) => a.order - b.order)
    .map(
      ({ activityId, title, category, difficulty, activityTypes }): PlanActivityRow => ({
        id: activityId as number,
        title,
        category,
        difficulty,
        activityTypes,
      })
    );
};

export const planTableToggleAriaLabel = (
  title: string,
  mode: "add" | "remove" | "blocked"
): string => {
  switch (mode) {
    case "remove":
      return `Remove ${title} from call plan`;
    case "blocked":
      return "Call plan is full; remove an activity to add another";
    case "add":
      return `Add ${title} to call plan`;
    default:
      return "";
  }
};

export const CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE =
  "The program has ended. You cannot create a new call plan.";

const uniqueProgramsFromCallPlans = (callPlans: CallPlanInfoFragment[]): Program[] => {
  const seenProgramIds = new Set<number>();
  return callPlans
    .map((cp) => cp.program)
    .filter((program) => {
      if (seenProgramIds.has(program.id)) {
        return false;
      }

      seenProgramIds.add(program.id);
      return true;
    });
};

/** Program whose date range includes today (or has no end date), or null if none. */
export const getActiveProgramFromCallPlans = (
  callPlans: CallPlanInfoFragment[]
): Program | null => {
  if (callPlans.length === 0) {
    return null;
  }

  const today = formatLocalCalendarDateToday();
  const activeProgram = uniqueProgramsFromCallPlans(callPlans).find((p) => {
    if (!p.end_date) {
      return true;
    }

    return p.start_date.slice(0, 10) <= today && p.end_date.slice(0, 10) >= today;
  });

  return activeProgram ?? null;
};

export const isCreateCallPlanBlockedByProgramEnd = (
  callPlans: CallPlanInfoFragment[],
  activeProgramHint?: Program | null
): boolean => {
  const activeProgram = activeProgramHint || getActiveProgramFromCallPlans(callPlans);
  if (activeProgram) {
    return false;
  }

  const today = formatLocalCalendarDateToday();
  const programs = uniqueProgramsFromCallPlans(callPlans);
  return programs.every((p) => p.end_date && p.end_date.slice(0, 10) < today);
};

const callPlansForActiveProgram = (
  callPlans: CallPlanInfoFragment[],
  activeProgram: Program | null
): CallPlanInfoFragment[] => {
  if (!activeProgram) {
    return [];
  }

  return callPlans.filter((cp) => cp.program.id === activeProgram.id);
};

/** Week number the backend will assign for the active program (display only; not sent by the form). */
export const getNextProgramWeekDisplay = (
  callPlans: CallPlanInfoFragment[],
  activeProgram: Program | null
): number => {
  const relevant = callPlansForActiveProgram(callPlans, activeProgram);
  if (relevant.length === 0) {
    return 1;
  }

  return Math.max(...relevant.map((cp) => cp.programWeek)) + 1;
};

export const computeEditFormValues = (callPlan: CallPlanInfoFragment): CallPlanFormValues => {
  const startDate = callPlan.startDate.slice(0, 10);
  return {
    startDate,
    endDate: callPlan.endDate?.slice(0, 10) ?? addCalendarDays(startDate, CALL_PLAN_DURATION_DAYS),
  };
};

export const computeDefaults = (
  callPlans: CallPlanInfoFragment[],
  activeProgram: Program | null
): CallPlanFormValues => {
  const relevant = callPlansForActiveProgram(callPlans, activeProgram);
  const today = formatLocalCalendarDateToday();
  if (relevant.length === 0) {
    return {
      startDate: today,
      endDate: addCalendarDays(today, CALL_PLAN_DURATION_DAYS),
    };
  }

  const latestEndDate = relevant.reduce<string | null>((latest, cp) => {
    if (!cp.endDate) {
      return latest;
    }
    if (!latest || cp.endDate > latest) {
      return cp.endDate;
    }

    return latest;
  }, null);
  const dayAfterLatest = latestEndDate && addCalendarDays(latestEndDate.slice(0, 10), 1);
  const startDate = dayAfterLatest && dayAfterLatest > today ? dayAfterLatest : today;

  return {
    startDate,
    endDate: addCalendarDays(startDate, CALL_PLAN_DURATION_DAYS),
  };
};

/** Inclusive range overlap in UTC (matches API start/end instants). */
const rangesOverlapUtc = (aStartIso: string, aEndIso: string, bStartIso: string, bEndIso: string) =>
  new Date(aStartIso).getTime() <= new Date(bEndIso).getTime() &&
  new Date(bStartIso).getTime() <= new Date(aEndIso).getTime();

const getCallPlanRangeOverlapError = (
  startDate: string,
  endDate: string,
  callPlans: CallPlanInfoFragment[],
  proposalStartIso?: string | null
): string | false => {
  const proposalStart = proposalStartIso ?? toStartDateTimeUtc(startDate);
  const proposalEnd = toEndDateTimeUtc(endDate);

  const overlapping = callPlans.find((cp) => {
    if (!cp.endDate) {
      return true;
    }

    return rangesOverlapUtc(proposalStart, proposalEnd, cp.startDate, cp.endDate);
  });

  return overlapping ? `Overlaps with week ${overlapping.programWeek}.` : false;
};

export const validateStartDate = (
  startDate: string,
  endDate: string,
  callPlans: CallPlanInfoFragment[],
  activeProgram: Program | null,
  /** When true, start date may be before today (e.g. active call plan; start is read-only in the UI). */
  isActiveCallPlan = false
): string | true => {
  if (isActiveCallPlan) {
    return true;
  }
  if (!startDate) {
    return "Start date is required.";
  }

  const today = formatLocalCalendarDateToday();
  if (startDate < today) {
    return "Start date cannot be in the past.";
  }
  if (!endDate) {
    return true;
  }
  if (startDate > endDate) {
    return "Must be before end date.";
  }
  if (activeProgram?.start_date && startDate < activeProgram.start_date.slice(0, 10)) {
    return "Start date cannot be before program start date.";
  }
  if (activeProgram?.end_date && startDate > activeProgram.end_date.slice(0, 10)) {
    return "Start date cannot be after program end date.";
  }

  const overlapError = getCallPlanRangeOverlapError(startDate, endDate, callPlans);
  if (overlapError) {
    return overlapError;
  }

  return true;
};

export const validateEndDate = (
  endDate: string,
  startDate: string,
  callPlans: CallPlanInfoFragment[],
  activeProgram: Program | null,
  /** Active plan: pass API `startDate`. */
  proposalStartIso?: string | null
): string | true => {
  if (!endDate) {
    return "End date is required.";
  }
  if (startDate && endDate < startDate) {
    return "Must be after start date.";
  }
  if (activeProgram?.end_date && endDate > activeProgram.end_date.slice(0, 10)) {
    return "End date cannot be after program end date.";
  }

  if (startDate && endDate) {
    const overlapError = getCallPlanRangeOverlapError(
      startDate,
      endDate,
      callPlans,
      proposalStartIso
    );
    if (overlapError) {
      return overlapError;
    }
  }

  return true;
};

/** Messages from a GraphQL execution result when present (e.g. partial mutation response). */
export const formatGqlExecutionErrors = (result: unknown): string | undefined => {
  if (!result || typeof result !== "object" || !("errors" in result)) {
    return undefined;
  }
  const errors = (result as { errors?: ReadonlyArray<{ message?: string }> }).errors;
  const msgs = errors?.map((err) => err.message).filter((m): m is string => Boolean(m?.trim()));
  if (!msgs?.length) {
    return undefined;
  }
  return msgs.join(" ");
};

/** Prefer GraphQL `errors[].message` from the server; then Error.message; then fallback. */
export const graphqlClientErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === "object") {
    const gqlErrs = (error as ApolloError).graphQLErrors;
    if (!gqlErrs?.length) {
      return fallback;
    }

    const msgs = gqlErrs.map((e) => e.message).filter((m): m is string => Boolean(m?.trim()));
    if (msgs.length > 0) {
      return msgs.join(" ");
    }
  }
  if (error instanceof Error) {
    const msg = error.message.trim();
    if (msg) {
      return msg;
    }
  }

  return fallback;
};
